import { useState, useCallback } from 'react';

interface UseFormCheckboxGroupProps {
    name: string;
    initialValue?: Set<string>;
    onFirstTouch?: (name: string) => void;
}

interface UseFormCheckboxGroupReturn {
    name: string;
    value: string[];
    onChange: (values: string[]) => void;
    onOtherChange: (newVal: string, prevVal: string) => void;
    list: Set<string>;
    touched: boolean;
}

const useFormCheckboxGroup = ({
    name,
    initialValue = new Set(),
    onFirstTouch,
}: UseFormCheckboxGroupProps): UseFormCheckboxGroupReturn => {
    const [list, setList] = useState<Set<string>>(initialValue);
    const [touched, setTouched] = useState<boolean>(false);

    const handleChange = useCallback(
        (values: string[]) => {
            if (!touched) {
                setTouched(true);
                onFirstTouch?.(name);
            }
            setList(new Set(values));
        },
        [touched, onFirstTouch, name]
    );

    const handleOtherChange = useCallback(
        (newVal: string, prevVal: string) => {
            const newList = new Set(list);
            if (prevVal !== '') {
                newList.delete(prevVal);
            }
            if (newVal !== '') {
                newList.add(newVal);
            }
            setList(newList);
        },
        [list]
    );

    return {
        name,
        value: Array.from(list),
        onChange: handleChange,
        onOtherChange: handleOtherChange,
        list,
        touched,
    };
};

export default useFormCheckboxGroup;
